/**
 * MJBizCon 2026 content — TEMPORARY in-repo source of truth for the COPY.
 *
 * Text lives here rather than in the CMS by explicit decision, to be migrated
 * later. scripts/seed-mjbc-2026.ts reads these same constants, so when the copy
 * does move to the CMS it is seeded from here and the two cannot drift apart.
 *
 * WHO IS INVITED IS NOT HERE. The guest list lives in the invite spreadsheet and
 * reaches the page at runtime through parts/useInvite.ts — see
 * docs/MJBC_SHEETS_SETUP.md. It used to be a hardcoded array published at
 * /mjbc-2026/<firstname>-<lastname>-<company>, which had two faults that only
 * bite once real invitations go out: the URL published the guest's name and
 * employer, and it CHANGED whenever a spelling was corrected, silently breaking
 * a link that had already been e-mailed. Every new guest was also a deploy,
 * because `output: 'export'` prerendered one page per slug.
 *
 * Copy transcribed from the Figma "Direct Link" flow. `[NAME]` in the design
 * becomes the partner name; visitors arriving without an invite link see
 * EVENT_COPY.fallbackName.
 */

export const EVENT_SLUG = 'mjbc-2026';

/** One guest, as the spreadsheet describes them. */
export interface EventPartner {
    firstName: string;
    lastName: string;
    company: string;
}

/**
 * Doors open. Drawn in the design as the stacked "12 / 02 / 26", and the instant
 * the T-minus counts down to.
 *
 * The offset is Las Vegas in December — Pacific STANDARD time, UTC-8 — written
 * out rather than left to the browser, so the countdown lands on the same
 * instant for a guest reading it from anywhere.
 */
export const EVENT_START = '2026-12-02T20:00:00-08:00';
/**
 * Doors close. The party runs 8:00 PM to midnight, so this is the small hours of
 * the 3rd — it is the end of the SAME evening, not a second day. It sizes the
 * calendar entry and nothing else; the countdown runs to EVENT_START.
 */
export const EVENT_END = '2026-12-03T00:00:00-08:00';

/**
 * The Google Calendar entry behind "mark your calendar", transcribed from the
 * client's invite-copy doc.
 *
 * It is the one piece of copy that outlives the page: it sits in the guest's
 * calendar for ten months, alone, with no artwork and no context around it. So
 * it names the party and says what it is in plain words, where EVENT_COPY.metaTitle
 * names the brand for a link preview and the frames talk in mission language.
 *
 * The `<b>` is Google's: its TEMPLATE link renders a small HTML subset in the
 * description, which is where the doc's bold survives. Blank lines between the
 * paragraphs come through as written.
 */
export const CALENDAR_COPY = {
    title: 'Ignition Sequence | MJBizCon 2026',
    description: [
        '<b>Prepare for liftoff.</b>',

        'Join ACTIVE & Small Circle for <b>Ignition Sequence</b>, the can’t-miss party during '
        + 'MJBizCon 2026. We’re bringing together some of the cannabis industry’s brightest '
        + 'stars for a night of music, connection, and a few unexpected big moments in Las Vegas.',

        'Venue coordinates revealed T-minus 24 hours from the start of the event. A short '
        + 'trajectory from the Strip.',

        'We’ll be sharing more exciting details about the event in the weeks to come.',
    ].join('\n\n'),
} as const;

/**
 * The Figma file holds two campaign stages, not one. They share the boot
 * sequence and the nameplate, then diverge:
 *
 *   'save-the-date'  DIRECT LINK_* frames 1-9      — teaser, ends at "mark your calendar"
 *   'formal-invite'  Formal Invite_Direct_* 01-13  — adds the crew manifest RSVP
 *
 * Copy that differs between them lives in STAGE_COPY below; everything shared
 * stays in EVENT_COPY.
 */
export type EventStage = 'save-the-date' | 'formal-invite';

/**
 * The artwork a link previews with when it is pasted into a chat, a message or a
 * mail client — ONE CARD PER CAMPAIGN STAGE, each carrying the same line the
 * briefing under it does.
 *
 * One card used to serve both, and what justified that was that the two stages
 * were indistinguishable to a scraper: the shared artwork was the invitation
 * e-mail's own GIF and said nothing about where a guest stood in the campaign.
 * The client has since drawn a card per stage, so the preview now follows the
 * page — the teaser previews "A NEW TRADITION STARTS HERE", the formal invite
 * "AN INTERSTELLAR INDUSTRY PARTY", word for word what STAGE_COPY.tagline reads
 * on each.
 *
 * The cost is the one the earlier note here refused: a forwarded link now tells
 * whoever it reaches which stage its guest is at, without their opening it. It
 * buys the client's stage-matched artwork rather than nothing, and it is by some
 * distance the smaller of the two things these previews give away — SHARE_PREVIEW
 * has carried the guest's NAME since the same links went personal.
 *
 * 1200x630, where the retired GIF was a 600x1000 portrait: that one was shown
 * whole by chat apps, cropped to a band across its middle by Facebook and
 * LinkedIn, and shown as its first frame everywhere. These are the landscape
 * stills that case called for, at the 1.91:1 the cropping platforms lay out to —
 * which is also why the cards are now worth a `summary_large_image` on X. See
 * metadata.ts.
 *
 * Served from OUR image CDN and uploaded outside the VadImage pipeline on
 * purpose: that pipeline makes WEBP renders in an `opt/` folder at fixed widths,
 * which is meant for pages, not for scrapers that want one unchanging URL. The
 * originals are the agency's "Social Share Images V3"; scripts/upload-r2-media.ts
 * put them there.
 */
export const EVENT_SHARE_IMAGES: Record<EventStage, {
    url: string;
    width: number;
    height: number;
    type: string;
    alt: string;
}> = {
    'save-the-date': {
        url: 'https://images.active710.com/mjbc-2026/active-mjbc-2026-share-save-the-date-v3.jpg',
        width: 1200,
        height: 630,
        type: 'image/jpeg',
        alt: 'IGNITION SEQUENCE — ACTIVE X Small Circle at MJBizCon 2026. '
            + 'A new tradition starts here. 12.02.26, Las Vegas.',
    },
    'formal-invite': {
        url: 'https://images.active710.com/mjbc-2026/active-mjbc-2026-share-formal-invite-v3.jpg',
        width: 1200,
        height: 630,
        type: 'image/jpeg',
        alt: 'IGNITION SEQUENCE — ACTIVE X Small Circle at MJBizCon 2026. '
            + 'An interstellar industry party. 12.02.26, Las Vegas.',
    },
};

/**
 * The words beside that artwork — the invitation as a chat app, a mail client or
 * Slack renders it when the link is pasted.
 *
 * PERSONAL, deliberately, which reverses the earlier decision that everything
 * about these links stay anonymous. The reference is the agency's own invitation
 * at mjbc.smallcircle.dev: it previews as the guest's name over one line naming
 * the party, and matching it is the point — the two invitations go out side by
 * side, and a guest holding both should not be able to tell which one is ours.
 *
 * What it costs is written down where it happens; see the lookup in
 * functions/mjbc-2026/[[token]].js. Briefly: a forwarded link now shows the
 * original guest's name to whoever it reaches, without anyone opening it. The
 * token was always the only thing guarding that name — this hands it to whoever
 * holds the link rather than only to someone who follows it.
 *
 * `title` is given the same "COMMANDER <name>" the nameplate shows — see
 * commanderName — and adds the comma the reference ends on, so the preview reads
 * as the opening of the sentence `description` finishes. Someone arriving without
 * an invite link is addressed by EVENT_COPY.fallbackName, exactly as the page
 * itself addresses them.
 */
export const SHARE_PREVIEW = {
    title: (name: string): string => `${name},`,
    description: 'You have been personally invited to IGNITION SEQUENCE presented by '
        + 'ACTIVE X Small Circle.',
} as const;

export const EVENT_COPY = {
    metaTitle: 'ACTIVE at MJBizCon 2026',
    metaDescription: 'ACTIVE® Vapor Devices at MJBizCon 2026 in Las Vegas. A new tradition starts here.',

    /** Boot sequence. Each label holds for an equal slice of the progress bar. */
    bootLabels: ['Signal loading...', 'Signal loading...', 'Frequency locking...', 'Link established...'],

    intendedFor: 'This message is intended for',
    chosen: ['You have been', 'chosen for the mission'],
    briefingCta: ['Review confidential', 'mission briefing'],

    /*
     * The launch's sound control, labelled by what pressing it DOES rather than
     * by what is happening — "sound on" appears while the clip is silent. It
     * doubles as the prompt on a browser that refused to start the clip with
     * sound, which is the reason it says anything at all rather than being a
     * bare speaker glyph.
     */
    soundOn: 'Sound on',
    soundOff: 'Sound off',

    reportForBoarding: 'Report for boarding',
    /** Rendered stacked on desktop and joined as "12.02.26" on mobile. */
    dateStack: ['12', '02', '26'],
    tMinus: 'T-minus:',
    transmissionFor: 'Top secret transmission for',

    /** Shown when someone reaches the landing without a partner link. */
    fallbackName: 'Commander',
} as const;

/** Copy that differs between the teaser and the formal invite. */
export const STAGE_COPY: Record<EventStage, {
    tagline: readonly [string, string];
    location: readonly [string, string];
    /** One entry per drawn line — see parts/Lines.tsx. */
    coordinates: readonly string[];
    /** Likewise: the button's label is drawn on set lines, not wrapped. */
    primaryCta: readonly string[];
}> = {
    'save-the-date': {
        tagline: ['a new tradition', 'starts here'],
        // The desktop frame abbreviates this to "NV" and the mobile one spells
        // it out; spelled out is the version to keep.
        location: ['Las Vegas, Nevada', 'planet Earth'],
        coordinates: ['Coordinates', 'classified'],
        // One line at both sizes — see the sheet's note on the button.
        primaryCta: ['mark your calendar'],
    },
    'formal-invite': {
        // Broken where the share card breaks it. The Figma metadata does not
        // record where the frame's two lines split, but the agency's own social
        // card sets this copy over "AN INTERSTELLAR" / "INDUSTRY PARTY" — see
        // EVENT_SHARE_IMAGES, which is the same words in the same face.
        tagline: ['an interstellar', 'industry party'],
        location: ['Las Vegas, Nevada', 'planet Earth'],
        // THREE lines, not two. The frame's coordinates box is 270 wide and
        // 68 tall — three lines of its 21.5 leading — and "DECLASSIFIED UPON"
        // measures 271.6 at 25/-0.25, so a two-line split cannot hold: the
        // browser took "UPON" down and made four lines of it, which is what
        // the client flagged. Mobile agrees at its own size: the same break
        // measures 163 against the frame's 164-wide box.
        coordinates: ['Coordinates', 'declassified upon', 'manifest lock'],
        // Not the teaser's words. This button opens the crew manifest rather
        // than a calendar, and the frame labels it with where it goes.
        //
        // Broken where the frame breaks it, which is not where the browser did:
        // the drawn label box is 193 wide over two lines and "IN THE CREW
        // MANIFEST" measures 192.7 at 15, so "IN" belongs at the head of line
        // two. Left to wrap inside the button it stayed on line one instead.
        primaryCta: ['Secure your place', 'in the crew manifest'],
    },
};

/** Frames 11-13: crew manifest RSVP, formal invite only. */
export const MANIFEST_COPY = {
    heading: 'Crew intake',
    /*
     * "* details required", not the frame's "ALL details required".
     *
     * The frame's line was true when it was drawn and stopped being true twice:
     * the guest-passes box has always been optional, and the client has since
     * asked for call sign to be as well. A form that says everything is required
     * over two fields that are not is the version people mistrust — and the
     * asterisk it now points at is already on the four labels that mean it.
     *
     * Shorter than the line it replaces at every breakpoint — 268.2 against
     * 288.4 at the desktop 14/-0.14 — so it cannot wrap where that one did not.
     * It does give up the frame's nicety of setting both lines to the same
     * width: the drawn pair measure 288.7 and 288.4, which is plainly deliberate
     * and which no accurate wording gets back. Being right beat being flush.
     */
    subheading: ['Complete your crew registration', '* details required for manifest'],

    /*
     * Starred where the field is mandatory, since a required field that does not
     * say so is the version people complain about. The frames star only three —
     * full designation, comms channels and crew affiliation — and EMAIL gained
     * one from the client's request that all fields be mandatory.
     *
     * CALL SIGN is the one that request has since given back: the client asked
     * for it to be optional, and it is the only starred field whose answer a
     * guest may genuinely not have. Someone with no alias for the mission can
     * now submit without inventing one.
     *
     * The guest-passes box below is likewise NOT starred; see guestsBody.
     */
    fullName: {label: 'Full designation:*', placeholder: 'Full name'},
    callSign: {label: 'Call sign:', placeholder: 'Your alias for the mission'},
    phone: {label: 'Comms channels*', placeholder: 'Cellphone'},
    email: {label: 'Email*', placeholder: 'Email address'},
    company: {label: 'Crew affiliation*', placeholder: 'Company name'},

    availability: 'Crew availability',
    availabilityHint: 'please confirm your status for the mission',

    guestsLead: 'Space on this mission is highly limited: we have secured space for you +1 guest.',
    guestsBody: 'Do you need more space on the mission for your crew? Please indicate your wish list for '
        + 'additional guest passes. Ground control will evaluate available capacity and be in touch to '
        + 'confirm if your request (or a portion of it) can be granted.',
    /*
     * The one field left optional against "make all fields mandatory".
     *
     * It asks whether the guest needs passes BEYOND the +1 they already have,
     * and its own copy frames it as a wish list. A guest who needs none has no
     * answer to give, so requiring it would stop them RSVPing at all — the
     * opposite of what the form is for. Flagged to the client; one `required`
     * away if they want it regardless.
     */
    guestsPlaceholder: 'Your answer',

    /*
     * The required consent box under the guest-passes answer, word for word from
     * the client's "Event Terms and Conditions" doc. Split around the link so the
     * link can be a real anchor rather than markup inside a string.
     *
     * The doc asks for the link to be "very conspicuous", so the sheet draws it
     * blue, bold and underlined, where everything else here is cyan or white.
     */
    terms: {
        lead: 'Registration is free, subject to capacity, and limited to guests age 21 or older. '
            + 'By registering, I represent that I am at least 21 years old and have read and agree to the ',
        link: 'Event Terms and Conditions',
        tail: '.',
    },

    submit: ['Submit to mission', 'crew manifest'],
    submitting: 'Transmitting...',
    error: 'Transmission failed. Please try again.',
} as const;

/**
 * The three availability options. `standDown` decides which outcome screen the
 * visitor lands on: frame 12 (cleared for launch) or frame 13 (standing down).
 * "Standing by" is mapped to frame 13 because its copy — "your status has been
 * logged" — fits an unconfirmed reply, where frame 12's "you're cleared for
 * launch" would be wrong. The design does not draw a third outcome.
 */
export const AVAILABILITY_OPTIONS = [
    {value: 'confirmed', label: 'Confirmed, reporting for duty', standDown: false},
    {value: 'unable', label: 'Unable to report, standing down', standDown: true},
    {value: 'standing-by', label: 'Standing by, awaiting confirmation', standDown: true},
] as const;

export type AvailabilityValue = typeof AVAILABILITY_OPTIONS[number]['value'];

/**
 * Frames 12 and 13. `[NAME]` in the design becomes the commander name.
 *
 * The bodies are line arrays, not sentences, because the guest's name is inside
 * them and a wrapped name is the one break nobody will accept: left to flow,
 * "COMMANDER MICHAEL SHUMILOV" split across two lines with the surname alone on
 * the second, which is what the client flagged. Giving the name its own line
 * puts "COMMANDER" at the head of line two — the client's own suggestion — and
 * makes the split impossible however long the name turns out to be.
 *
 * The frames' three lines come from measuring the short "[NAME]" placeholder
 * and do not survive a real name, so the breaks below follow the client's note
 * rather than the drawn line count. Frame 13 shows the design's answer to the
 * same problem — it widens its whole block from 569 to 700 for the longer copy
 * — which the sheet now does too.
 */
export const OUTCOME_COPY = {
    confirmed: {
        heading: 'Crew manifest locked',
        body: (name: string): readonly string[] => [
            'Ignition sequence initiated.',
            `${name},`,
            "you're cleared for launch.",
            'Stand by for pre-flight checks.',
        ],
    },
    standDown: {
        heading: 'Standing down',
        // The frame breaks this after the name and again after "LOGGED." — its
        // layer name carries the double spaces. The closing sentences are left
        // to fill the block, as they are drawn.
        body: (name: string): readonly string[] => [
            `${name},`,
            'your status has been logged.',
            'We hope your orbit brings you back for the next mission. '
            + 'Should your mission parameters change, contact your assigned officer.',
        ],
    },
} as const;

/** The rank the design puts in front of every name. */
const RANK = 'Commander';

/** The design renders every name as "COMMANDER <name>". */
export const commanderName = (partner?: EventPartner): string =>
    partner === undefined
        ? EVENT_COPY.fallbackName
        : `${RANK} ${partner.firstName} ${partner.lastName}`;

/**
 * The same name, split into the rank and the person — which only the nameplate
 * needs.
 *
 * Frame 05 draws the nameplate as one line because it draws "[NAME]". A real
 * name does not fit the 252 inside the drawn 280 box — "COMMANDER MICHAEL
 * SHUMILOV" measures 364.6 — so it wraps, and where it wraps is the problem the
 * client raised: "COMMANDER MICHAEL" measures 244.8, fits, and leaves the
 * surname alone on the second line.
 *
 * Splitting the two here lets the sheet break after the rank on mobile and keep
 * both parts of the person's name together below it. Doing it structurally
 * rather than by tightening the box matters because the box would only be tuned
 * for THIS name: a guest with a shorter first name would fit "COMMANDER
 * ALEXANDRA" and strand their surname all over again.
 */
export const commanderParts = (partner?: EventPartner): readonly [string, string] =>
    partner === undefined
        ? [EVENT_COPY.fallbackName, '']
        : [RANK, `${partner.firstName} ${partner.lastName}`];

/**
 * Where the crew manifest's consent box links to. Its own page, as the client's
 * doc allows ("the T&C can be on a separate page that’s only visible if someone
 * clicks the link"), so the form stays the length the frame draws.
 */
export const TERMS_PATH = `/${EVENT_SLUG}/terms`;

/**
 * Which text a guest agreed to. Sent with every reply and stored beside the
 * acceptance, because a record that says "accepted" without saying WHAT is not
 * much of a record once the terms have been revised.
 *
 * Bump it whenever EVENT_TERMS changes by so much as a word. It is the date the
 * text was supplied, not a build number, so it can be read against the client's
 * own copy of the doc.
 *
 * Mirrored in functions/api/mjbc/[[route]].js, which files THAT value with each
 * reply rather than the one the browser sends — bump both together.
 */
export const TERMS_VERSION = '2026-09-21';

/** One paragraph, optionally set bold, or one bulleted list. */
export type TermsBlock =
    | {kind: 'p'; text: string; strong?: boolean}
    | {kind: 'list'; items: readonly string[]};

const p = (text: string): TermsBlock => ({kind: 'p', text});
/** The doc sets its three all-caps waiver paragraphs in bold. That is what keeps
 *  them conspicuous, which is a legal point rather than a styling one. */
const strong = (text: string): TermsBlock => ({kind: 'p', text, strong: true});
const list = (...items: string[]): TermsBlock => ({kind: 'list', items});

/**
 * The Event Terms and Conditions, transcribed from the client's doc of that name.
 *
 * Only the part under the "Event Terms and Conditions" heading. The notes above it
 * in the doc — the checkbox wording and the instructions about it — were for us
 * and were dropped at the client's request; the checkbox wording lives in
 * MANIFEST_COPY.terms.
 */
export const EVENT_TERMS: {
    heading: string;
    intro: string;
    sections: readonly {heading: string; blocks: readonly TermsBlock[]}[];
} = {
    heading: 'Event Terms and Conditions',
    intro: 'These Event Terms and Conditions (“Terms”) apply to your registration for and attendance at '
        + 'Ignition Sequence (the “Event”), hosted by Next Level Ventures LLC, dba ACTIVE (“ACTIVE”) and '
        + 'Global Creative Artists, LLC dba Small Circle (“Small Circle”) at Fresh Wata, LLC, dba StarBase '
        + '(the “Venue”). By checking the acceptance box during registration, you agree to these Terms.',
    sections: [
        {
            heading: '1. Age and Identification',
            blocks: [
                p('You represent and warrant that you are at least 21 years old. You must present valid, '
                    + 'government-issued photo identification upon request. ACTIVE or the Venue may deny entry '
                    + 'if your identity or age cannot be verified.'),
            ],
        },
        {
            heading: '2. Event and Venue Rules',
            blocks: [
                p('You must comply with all applicable laws and all policies, procedures, security measures, '
                    + 'signs, and instructions issued by ACTIVE, Small Circle, the Venue, or their personnel.'),
                p('Cannabis may be possessed or consumed only as expressly permitted by applicable law and Venue '
                    + 'policy and only in designated cannabis-consumption areas. You may not bring unauthorized '
                    + 'cannabis onto the premises or remove cannabis from a designated consumption area. Vapor '
                    + 'products and alcohol may be used or consumed only where and as permitted by ACTIVE and the '
                    + 'Venue.'),
                p('You may not drive or operate a vehicle while impaired.'),
            ],
        },
        {
            heading: '3. Admission and Removal',
            blocks: [
                p('Registration does not guarantee admission. ACTIVE and the Venue may, in their sole discretion, '
                    + 'deny entry to or remove any person for any lawful reason or without stating a reason, '
                    + 'including:'),
                list(
                    'capacity or space limitations;',
                    'safety, security, or operational concerns;',
                    'intoxication or impairment;',
                    'disruptive, threatening, unsafe, unlawful, offensive, or inappropriate behavior;',
                    'inappropriate attire;',
                    'failure to provide acceptable identification; or',
                    'failure to comply with these Terms or Event or Venue instructions.',
                ),
                p('Because registration is free, denial of entry or removal does not entitle you to compensation '
                    + 'or other relief.'),
            ],
        },
        {
            heading: '4. Media Consent',
            blocks: [
                p('You understand that ACTIVE, Small Circle, the Venue, sponsors, and their designees may '
                    + 'photograph, film, livestream, or otherwise record the Event.'),
                p('You irrevocably authorize them to record, reproduce, edit, publish, display, distribute, and '
                    + 'otherwise use your name, image, likeness, voice, statements, and appearance in photographs, '
                    + 'video, audio, and other Event content for advertising, promotional, social-media, '
                    + 'public-relations, archival, and other lawful purposes, in any media, worldwide and in '
                    + 'perpetuity, without compensation, notice, inspection, or further approval.'),
                p('You waive claims based on the authorized use of this content, including claims for '
                    + 'infringement of privacy, publicity, or similar rights, to the fullest extent permitted by '
                    + 'law. Attendance constitutes consent to recording, and no individual opt-out is available.'),
            ],
        },
        {
            heading: '5. Assumption of Risk',
            blocks: [
                strong('YOU VOLUNTARILY ASSUME ALL RISKS ASSOCIATED WITH REGISTERING FOR, TRAVELING TO, ATTENDING, '
                    + 'AND PARTICIPATING IN THE EVENT, INCLUDING RISKS ARISING FROM CROWDS, SLIPS OR FALLS, LOUD '
                    + 'MUSIC, LIGHTING, SECURITY PROCEDURES, ALCOHOL OR CANNABIS CONSUMPTION, SMOKE OR VAPOR '
                    + 'EXPOSURE, CONTACT WITH IMPAIRED PERSONS, PRODUCTS PROVIDED OR DISPLAYED AT THE EVENT, AND '
                    + 'OTHER KNOWN OR UNKNOWN EVENT-RELATED CONDITIONS.'),
                p('You are responsible for deciding whether to consume or use any product and for monitoring your '
                    + 'own condition. Attendance does not constitute a representation regarding the safety, '
                    + 'legality, quality, or suitability of any product.'),
            ],
        },
        {
            heading: '6. Release of Liability',
            blocks: [
                strong('TO THE FULLEST EXTENT PERMITTED BY LAW, YOU RELEASE AND FOREVER DISCHARGE THE RELEASED '
                    + 'PARTIES FROM ALL CLAIMS, DEMANDS, CAUSES OF ACTION, DAMAGES, LOSSES, COSTS, AND LIABILITIES '
                    + 'ARISING FROM OR RELATING TO YOUR REGISTRATION FOR, TRAVEL TO, ATTENDANCE AT, OR '
                    + 'PARTICIPATION IN THE EVENT, INCLUDING CLAIMS FOR PERSONAL INJURY, ILLNESS, DEATH, PROPERTY '
                    + 'DAMAGE, PRODUCT-RELATED INJURY, OR OTHER LOSS, EVEN IF CAUSED IN WHOLE OR IN PART BY THE '
                    + 'ORDINARY NEGLIGENCE OF A RELEASED PARTY.'),
                p('“Released Parties” means ACTIVE, Small Circle, the Venue, the property owner, Event sponsors, '
                    + 'and each of their respective affiliates, owners, officers, directors, employees, agents, '
                    + 'contractors, vendors, security providers, event producers, representatives, successors, '
                    + 'and assigns.'),
                p('This release does not apply to liability that cannot lawfully be released, including liability '
                    + 'for gross negligence, recklessness, or intentional misconduct to the extent applicable law '
                    + 'prohibits its release.'),
            ],
        },
        {
            heading: '7. Limitation of Liability',
            blocks: [
                p('To the extent any claim is not subject to the release above:'),
                list(
                    'the Released Parties will not be liable for indirect, incidental, consequential, special, '
                    + 'exemplary, or punitive damages; and',
                    'the aggregate liability of all Released Parties arising from the Event will not exceed $100.',
                ),
                p('These limitations apply regardless of the legal theory asserted and to the fullest extent '
                    + 'permitted by law.'),
            ],
        },
        {
            heading: '8. Personal Property',
            blocks: [
                p('You are responsible for your personal property. The Released Parties are not responsible for '
                    + 'lost, stolen, misplaced, abandoned, or damaged property, including property left with '
                    + 'coat-check or security personnel, except to the extent liability cannot lawfully be '
                    + 'limited.'),
            ],
        },
        {
            heading: '9. Security Screening',
            blocks: [
                p('You consent to reasonable identity verification, security screening, bag inspection, and '
                    + 'enforcement of prohibited-item rules. Refusal to cooperate may result in denial of entry or '
                    + 'removal.'),
                p('ACTIVE, the Venue, and security personnel may confiscate, secure, or require removal of '
                    + 'prohibited or unsafe items as permitted by law.'),
            ],
        },
        {
            heading: '10. Unauthorized Activities',
            blocks: [
                p('Without ACTIVE’s prior written approval, you may not:'),
                list(
                    'conduct commercial activity, promotions, product distribution, or solicitation;',
                    'sell or distribute goods, cannabis, alcohol, tickets, or credentials;',
                    'engage in professional or commercial photography or recording;',
                    'livestream the Event;',
                    'use drones or similar recording equipment; or',
                    'use the Event’s, ACTIVE’s, Small Circle’s, the Venue’s, or any sponsor’s names, logos, or '
                    + 'branding for commercial purposes.',
                ),
                p('Personal, noncommercial photography is permitted only to the extent allowed by Event and Venue '
                    + 'policies.'),
            ],
        },
        {
            heading: '11. Governing Law and Disputes',
            blocks: [
                p('Nevada law governs these Terms. Any action arising from the Event must be brought exclusively '
                    + 'in the state or federal courts located in Clark County, Nevada.'),
                strong('TO THE FULLEST EXTENT PERMITTED BY LAW, YOU WAIVE THE RIGHT TO A JURY TRIAL AND AGREE TO '
                    + 'BRING CLAIMS ONLY IN YOUR INDIVIDUAL CAPACITY, RATHER THAN AS A PLAINTIFF OR CLASS MEMBER IN '
                    + 'A CLASS, COLLECTIVE, CONSOLIDATED, OR REPRESENTATIVE ACTION.'),
            ],
        },
        {
            heading: '12. General',
            blocks: [
                p('These Terms constitute the agreement governing your registration and attendance. If any '
                    + 'provision is unenforceable, it will be enforced to the maximum extent permitted, and the '
                    + 'remaining provisions will remain effective.'),
                p('The Released Parties are intended third-party beneficiaries of these Terms and may enforce the '
                    + 'provisions benefiting them.'),
            ],
        },
    ],
};
